import { useMutation } from '@tanstack/react-query';
import { AiFeedbackInsert } from '@/app/lib/supabase/types';
import { createClient } from '@/app/utils/supabase/client';
import toast from 'react-hot-toast';

interface CreateFeedbackParams {
  sessionId: string;
  messageId?: string;
  type: 'bug' | 'feature_request' | 'improvement' | 'incorrect_response' | 'other';
  priority?: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  userComment?: string;
}

export function useAIFeedbackMutation() {

  const createFeedback = useMutation({
    mutationFn: async (params: CreateFeedbackParams) => {
      const supabase = createClient();

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get company_id from session
      const { data: session, error: sessionError } = await supabase
        .from('ai_sessions')
        .select('company_id')
        .eq('id', params.sessionId)
        .single();

      if (sessionError || !session) {
        throw new Error('Session not found');
      }

      // Prepare feedback data
      const feedbackData: AiFeedbackInsert = {
        session_id: params.sessionId,
        message_id: params.messageId || null,
        user_id: user.id,
        company_id: session.company_id,
        source: 'user_initiated',
        type: params.type,
        priority: params.priority || 'medium',
        title: params.title,
        description: params.description,
        user_comment: params.userComment || null,
        context: {
          reported_at: new Date().toISOString(),
          user_agent: navigator.userAgent,
        },
      };

      // Insert feedback
      const { data, error } = await supabase
        .from('ai_feedback')
        .insert(feedbackData)
        .select()
        .single();

      if (error) {
        console.error('Error creating feedback:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      toast.success('Thank you for your feedback!');
    },
    onError: (error) => {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback');
    },
  });

  return {
    createFeedback,
  };
}